import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../ui/card';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Button } from '../../ui/button';
import { Alert, AlertDescription } from '../../ui/alert';
import { Loader2, Zap, Share2 } from 'lucide-react';

const PROVIDER_LABEL = {
  openai: 'OpenAI',
  gemini: 'Google Gemini',
  anthropic: 'Anthropic Claude',
  azure: 'Azure OpenAI',
};

const ProviderCard = ({ provider, title, keyField, fields, children, ctx }) => {
  const { settings, handleInputChange, aiTesting, aiTestResult, handleAiTest } = ctx;
  const result = aiTestResult?.[provider];
  return (
    <Card className="card-shadow" data-testid={`settings-ai-${provider}-card`}>
      <CardHeader>
        <CardTitle className="text-base">{title}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="space-y-2">
          <Label htmlFor={keyField}>API-Key</Label>
          <Input id={keyField} name={keyField} type="password"
            value={settings[keyField] || ''} onChange={handleInputChange}
            placeholder="Leer lassen um bestehenden Key zu behalten"
            className="font-mono text-sm"
            data-testid={`settings-ai-${provider}-key`} />
        </div>
        {fields.map(f => (
          <div className="space-y-2" key={f.name}>
            <Label htmlFor={f.name}>{f.label}</Label>
            <Input id={f.name} name={f.name}
              value={settings[f.name] || ''} onChange={handleInputChange}
              placeholder={f.placeholder || ''}
              data-testid={`settings-ai-${provider}-${f.name}`} />
            {f.hint && <p className="text-xs text-[hsl(var(--muted-foreground))]">{f.hint}</p>}
          </div>
        ))}
        {children}
        <div className="pt-2 flex items-center gap-3">
          <Button type="button" variant="outline"
            onClick={() => handleAiTest(provider)}
            disabled={aiTesting === provider}
            data-testid={`settings-ai-${provider}-test-btn`}>
            {aiTesting === provider ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Zap className="w-4 h-4 mr-2" />}
            Verbindung testen
          </Button>
          {result && (
            <Alert variant={result.type === 'success' ? 'default' : 'destructive'} className="flex-1 py-2">
              <AlertDescription className="text-xs">{result.message}</AlertDescription>
            </Alert>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

const SettingsAi = ({ ctx }) => {
  const { settings, handleInputChange, aiFeatures } = ctx;
  const providers = aiFeatures?.providers?.length ? aiFeatures.providers : ['openai', 'gemini', 'anthropic', 'azure'];
  const features = aiFeatures?.features || [];

  return (
    <>
      <Card className="card-shadow" data-testid="settings-ai-intro">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Share2 className="w-5 h-5" />
            AI / LLM Provider
          </CardTitle>
          <CardDescription>
            Alle KI-Features laufen über deine eigenen API-Keys – nichts über die Emergent-Infrastruktur.
            Pro Feature wählst du unten den Provider; wenn der zugehörige Key leer ist, schlägt der Call hart fehl.
          </CardDescription>
        </CardHeader>
      </Card>

      <Card className="card-shadow" data-testid="settings-ai-features-card">
        <CardHeader>
          <CardTitle className="text-base">Feature-Zuordnung</CardTitle>
          <CardDescription>Welcher Provider soll welches Feature bedienen?</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {features.map(f => (
            <div key={f.key} className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-center">
              <Label className="font-medium">{f.label}</Label>
              <span className="text-xs text-[hsl(var(--muted-foreground))]">
                {f.kind === 'image' ? 'Bildgenerierung' : 'Textgenerierung'}
              </span>
              <select name={`ai_feature_${f.key}`}
                value={settings[`ai_feature_${f.key}`] || ''}
                onChange={handleInputChange}
                className="rounded-md border border-[hsl(var(--border))] bg-background px-3 py-2 text-sm"
                data-testid={`settings-ai-feature-${f.key}`}>
                {providers
                  .filter(p => !(f.kind === 'image' && p === 'anthropic'))
                  .map(p => <option key={p} value={p}>{PROVIDER_LABEL[p] || p}</option>)}
              </select>
            </div>
          ))}
          {features.length === 0 && (
            <p className="text-xs text-[hsl(var(--muted-foreground))]">Lade Features…</p>
          )}
        </CardContent>
      </Card>

      <ProviderCard provider="openai" title="OpenAI" keyField="ai_openai_api_key" ctx={ctx} fields={[
        { name: 'ai_openai_text_model', label: 'Text-Modell', placeholder: 'gpt-4o', hint: 'z. B. gpt-4o, gpt-4o-mini, gpt-5.2' },
        { name: 'ai_openai_image_model', label: 'Bild-Modell', placeholder: 'gpt-image-1', hint: 'z. B. gpt-image-1' },
      ]} />

      <ProviderCard provider="gemini" title="Google Gemini" keyField="ai_gemini_api_key" ctx={ctx} fields={[
        { name: 'ai_gemini_text_model', label: 'Text-Modell', placeholder: 'gemini-2.0-flash', hint: 'z. B. gemini-2.0-flash, gemini-1.5-pro' },
        { name: 'ai_gemini_image_model', label: 'Bild-Modell (Nano Banana)', placeholder: 'gemini-2.5-flash-image', hint: 'z. B. gemini-2.5-flash-image' },
      ]} />

      <ProviderCard provider="anthropic" title="Anthropic Claude" keyField="ai_anthropic_api_key" ctx={ctx} fields={[
        { name: 'ai_anthropic_text_model', label: 'Text-Modell', placeholder: 'claude-sonnet-4-5', hint: 'Anthropic bietet keine Bildgenerierung.' },
      ]} />

      <ProviderCard provider="azure" title="Azure OpenAI" keyField="ai_azure_api_key" ctx={ctx} fields={[
        { name: 'ai_azure_endpoint', label: 'Endpoint', placeholder: 'https://my-azure.openai.azure.com' },
        { name: 'ai_azure_api_version', label: 'API-Version', placeholder: '2024-10-21' },
        { name: 'ai_azure_text_deployment', label: 'Text-Deployment', placeholder: 'gpt-4o-deployment' },
        { name: 'ai_azure_image_deployment', label: 'Bild-Deployment', placeholder: 'dalle-3-deployment' },
      ]} />
    </>
  );
};

export default SettingsAi;
